/*------------------------------------------------------------------------------
   About      : Classification vocabularies owned by quill-fe (epic DA-697)

                These are the enum-ish fields quill stores as plain strings and
                serves **no catalog for** — unlike `types`, `groups` and
                `derivedQuantities`, which come from `getInsightCatalog` and must
                always be resolved through it.

                They live here rather than inside the form because the form is
                not their only consumer: the list table and the filter drawer
                need the same labels. While these were module-private to
                `InsightClassificationForm.tsx`, the table had no way to reach
                them and rendered raw values — `BILLING_CYCLE`, `OFF_PEAK`,
                `HIGHEST`, `NEGATIVE` — straight into the DOM, breaking the one
                rule this screen has everywhere else: **never render a raw enum
                name.**

                ⚠️ The stored value is `value`, never `label`. Renaming a
                `value` does not migrate records that already carry it — add a
                new entry instead.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/

export interface ClassificationOption {
  /** Stored on the record. */
  value : string
  /** Shown to the user. */
  label : string
}

/*------------------------------------------------------------------------------
   Vocabularies
------------------------------------------------------------------------------*/

export const MEASUREMENT_TYPES : ClassificationOption[] = [
  { value : 'ELECTRIC', label : 'Electric' },
  { value : 'GAS',      label : 'Gas' },
  { value : 'WATER',    label : 'Water' },
]

export const VALIDITY_TYPES : ClassificationOption[] = [
  { value : 'BILLING_CYCLE', label : 'Billing cycle' },
  { value : 'WEEKLY',        label : 'Weekly' },
  { value : 'MONTHLY',       label : 'Monthly' },
  { value : 'SEASONAL',      label : 'Seasonal' },
]

export const TIME_OF_USE : ClassificationOption[] = [
  { value : 'TOTAL',    label : 'Total' },
  { value : 'PEAK',     label : 'Peak' },
  { value : 'OFF_PEAK', label : 'Off peak' },
  { value : 'MID_PEAK', label : 'Mid peak' },
]

export const DIRECTIONS : ClassificationOption[] = [
  { value : 'HIGHEST', label : 'Highest' },
  { value : 'LOWEST',  label : 'Lowest' },
]

export const SENTIMENTS : ClassificationOption[] = [
  { value : 'POSITIVE', label : 'Positive' },
  { value : 'NEGATIVE', label : 'Negative' },
  { value : 'NEUTRAL',  label : 'Neutral' },
]

/** Used by both `pickUnitType` and `compareUnitType`. */
export const DURATION_TYPES : ClassificationOption[] = [
  { value : 'BILLING_CYCLE', label : 'Billing cycle' },
  { value : 'DAY',           label : 'Day' },
  { value : 'WEEK',          label : 'Week' },
  { value : 'MONTH',         label : 'Month' },
  { value : 'SEASON',        label : 'Season' },
]

/**
 * Insight category.
 *
 * Deliberately a fixed list rather than one derived from the values already on
 * records: deriving would make a single typo a permanent dropdown option.
 * Values match the categories in the design mockups (`image-4.png` /
 * `image-5.png` in the bidgely-quill repo root).
 */
export const INSIGHT_CATEGORIES : ClassificationOption[] = [
  { value : 'COMMUNITY', label : 'Community' },
  { value : 'SEASONAL',  label : 'Seasonal'  },
  { value : 'PROACTIVE', label : 'Proactive' },
  { value : 'TRENDS',    label : 'Trends'    },
]

/*------------------------------------------------------------------------------
   Labelling
------------------------------------------------------------------------------*/

/**
 * Display text for a stored value.
 *
 * Falls back to the raw value rather than blanking it. A record may carry a
 * value authored before the list existed, or set through the Strapi admin UI —
 * quill accepts any string for these fields — and an empty cell would read as
 * "unset" rather than "unrecognised".
 */
export function labelFor(
  options : ClassificationOption[],
  value   : string | null | undefined,
): string {
  if (!value) return ''
  return options.find((option) => option.value === value)?.label ?? value
}

export const insightCategoryLabel = (value: string | null | undefined) =>
  labelFor(INSIGHT_CATEGORIES, value)
